import React, { useEffect, useState } from 'react';
import useDocumentCache from '../../hooks/useDocumentCache';
import { setCachedPreview } from '../../app/EditorSlice';
import Header from '../Header/Header';
import Headers from '../Headers/Headers';
import Toolbar from '../Toolbar/Toolbar';
import './MainLayout.css';
import PlaygroundApp from '../lexical';
import MouseContainer from '../mousecontainer';
import AiChatWrapper from '../AiChatWrapper/AiChatWrapper';
import { useSelector, useDispatch } from 'react-redux';
import { fetchIpAddress, getLocationCoordinates, isEffectivelyOffline, loadTemplateIfAvailable, removeOfflineData } from '../../utils/helper';
import { message } from 'antd';
import Loader from '../SkeletonLoader/Loader';
import OfflineStatus from '../OfflineStatus';
import { EyeOutlined } from '@ant-design/icons';
import DocumentDataConflictModal from '../ConflictModal';
import { useParams, useSearchParams } from 'react-router-dom';
import { padServices } from '../../api.js';
import { setServerData, setIsConflictModalVisible, setTemplateData } from '../../app/EditorSlice';
import { closeCreatePadDrawer } from '../../app/EditorSlice';
import CreatePadDrawer from '../Dashboard/components/CreatePadDrawer/CreatePadDrawer';
import { getLocalDocument } from '../../utils/localDocuments';

const MainLayout = () => {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const isLocalDocument = searchParams.get('local') === 'true';
    const dispatch = useDispatch();
    const editorIdArr = useSelector((state) => state.editor.editorInstancesIds);
    const isReadOnly = useSelector((state) => state.editor.isReadOnly);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isOutlineOpen, setIsOutlineOpen] = useState(false);
    const [contentLoading, setContentLoading] = useState(true);
    const conflictPreviewVerion = useSelector((state) => state.editor.conflictPreviewVerion);
    const modals = useSelector((state) => state.editor.modals);
    const isTemplateDocument = useSelector((state) => state.editor.isTemplateDocument);
    const serverData = useSelector((state) => state.editor.serverData);
    const documentInformation = useSelector((state) => state.editor.documentInformation);
    const editorSummary = useSelector((state) => state.editor.editorSummary);
    const signatures = useSelector((state) => state.editor.signatures);
    const currentEditorData = useSelector((state) => state.editor.currentEditorData);

    // Caching applies to normal documents only (skip templates / read-only /
    // conflict-preview). Offline/external flows never reach this component path.
    const cacheEnabled = !isTemplateDocument && !isReadOnly && !conflictPreviewVerion && !isLocalDocument;
    const { cached, isUiLocked, lockUi, unlockUi, cacheRenderedRef, commitCache } =
        useDocumentCache(id, cacheEnabled);

    // True from the very first render when a renderable cache entry exists, so the
    // Header + chrome render instantly and the loader never flashes. Mirrors the
    // top component's gating (editable, non-template branch).
    const isDocumentCached = cacheEnabled && !!cached;
    const showChrome = !contentLoading || isDocumentCached;

    // Instant paint: seed the editor with cached content exactly once. We do NOT
    // mark the editor "ready" — the existing init path swaps in fresh data when
    // the background fetch resolves.
    useEffect(() => {
        if (cached?.documentData && !cacheRenderedRef.current) {
            cacheRenderedRef.current = true;
            dispatch(setCachedPreview(cached.documentData));
            lockUi();
        }
    }, [cached, dispatch, lockUi, cacheRenderedRef]);

    // Unlock edits once the background fetch finishes (fresh data has replaced the
    // cached preview).
    useEffect(() => {
        if (!contentLoading && isUiLocked) {
            unlockUi();
        }
    }, [contentLoading, isUiLocked, unlockUi]);

    // Write-back: mirror the latest user-visible state into the cache. Driven by
    // in-memory content so unsaved edits win over stale API data, and skipped while
    // the locked preview is showing so we never write the preview back.
    useEffect(() => {
        if (!cacheEnabled || isUiLocked || contentLoading) return;
        if (!serverData || Object.keys(serverData).length === 0) return;

        const documentData = {
            ...serverData,
            milepadData: currentEditorData || serverData.milepadData || "",
            summary: editorSummary || serverData.summary || "",
            Signatures: (signatures && signatures.length) ? signatures : (serverData.Signatures || []),
        };
        const documentInfo = (documentInformation && Object.keys(documentInformation).length)
            ? documentInformation
            : undefined;
        commitCache(documentData, documentInfo);
    }, [cacheEnabled, isUiLocked, contentLoading, serverData, currentEditorData,
        editorSummary, signatures, documentInformation, commitCache]);


    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const fetchEditorData = async () => {
            try {
                // Local documents load straight from localStorage — no server fetch.
                if (isLocalDocument) {
                    const record = getLocalDocument(id);
                    const content = record?.content || {};
                    dispatch(setServerData({
                        summary: content.summary || "",
                        Signatures: content.Signatures || [],
                        milepadData: content.milepadData || "",
                    }));
                    setContentLoading(false);
                    return;
                }

                if (isTemplateDocument) {
                    // For templates, fetch directly and set data without conflict checks
                    const serverResponse = await padServices.fetchTemplateById(id);
                    dispatch(setTemplateData(serverResponse?.data?.data));
                    let dataToLoad = {
                        ...serverResponse?.data,
                        summary: "",
                        Signatures: [],
                        milepadData: serverResponse?.data?.data?.milepadData || "",
                    };
                    dispatch(setServerData(dataToLoad));
                    setContentLoading(false);
                    return;
                }

                const offlineData = JSON.parse(localStorage.getItem("offlineMilepadData") || "{}");
                const localContent = offlineData[id]?.data || null;
                const localUpdateTime = offlineData[id]?.lastUpdateTime ? new Date(offlineData[id].lastUpdateTime) : null;
                const templateId = searchParams.get('templateId');
                const serverResponse = templateId
                    ? await loadTemplateIfAvailable({
                        searchParams,
                        setSearchParams,
                        id,
                        dispatch,
                        setContentLoading
                    })
                    : await padServices.fetchById(id);
                const serverUpdateTime = serverResponse?.data?.updatedAt ? new Date(serverResponse?.data?.updatedAt) : null;

                let dataToLoad = {
                    ...serverResponse?.data,
                    summary: serverResponse?.data?.summary || "",
                    Signatures: serverResponse?.data?.Signatures || [],
                    milepadData: serverResponse?.data?.milepadData || "",
                };

                if (serverResponse) {
                    dispatch(setServerData(dataToLoad));

                    if (!serverResponse.data) {
                        await padServices.saveToDatabase(id, '');
                        setContentLoading(false);
                    }
                    // Handle preview mode
                    if (conflictPreviewVerion) {
                        if (conflictPreviewVerion === "offline" && localContent) {
                            dataToLoad = {
                                ...dataToLoad,
                                summary: localContent.summary || "",
                                milepadData: localContent.milepadData || "",
                            };
                        }
                        dispatch(setServerData(dataToLoad));
                        setContentLoading(false);
                        return;
                    }

                    // Conflict scenario: both exist, but server data is newer
                    if (localContent && localContent?.milepadData && serverUpdateTime && localUpdateTime && serverUpdateTime > localUpdateTime) {
                        dispatch(setIsConflictModalVisible(true));
                        setContentLoading(false);
                        return;
                    }

                    // Conflict scenario: both exist, but local data is newer
                    if (localContent && localContent?.milepadData && serverUpdateTime && localUpdateTime && serverUpdateTime < localUpdateTime) {
                        dataToLoad = {
                            ...dataToLoad,
                            summary: localContent.summary,
                            milepadData: localContent.milepadData,
                        };
                        dispatch(setServerData(dataToLoad));
                        if (!isEffectivelyOffline()) {
                            await padServices.saveToDatabase(id, dataToLoad.milepadData, dataToLoad.summary, dispatch);
                            removeOfflineData(id);
                        }
                        setContentLoading(false);
                        return;
                    }

                    // Only server data available
                    if (serverResponse.data) {
                        setContentLoading(false);
                        return;
                    }
                }
            } catch (error) {
                if (error.response?.data?.responseCode === 1008) {
                    window.location.pathname = "/invalid-document"
                }
                console.error('Failed to fetch initial editor state:', error);
                setContentLoading(false);
            }
        };

        if (id) {
            fetchEditorData();
        }
    }, [id, isTemplateDocument]);

    const shellClassName = `doc-shell ${isReadOnly ? 'read-only-mode' : ''}`;

    if (!showChrome) {
        return (
            <Loader />
        )
    }

    else {
        return (
            // <div>
            <div className={shellClassName}>
                {!conflictPreviewVerion && <Header isMobile={isMobile} />}

                <div className='offlineStatusBar'>
                    <OfflineStatus />
                </div>

                {/* Toolbar should appear directly under header */}
                {!conflictPreviewVerion && !isReadOnly && <Toolbar />}

                <div className='main-body'>
                    <div className='doc-workspace'>
                        <div className='outline-panel'>
                            <Headers isMobile={isMobile} isOpenOutline={isOutlineOpen} setIsOpenOutline={setIsOutlineOpen} />
                        </div>

                        <div className='editor-panel'>
                            {editorIdArr.map((id) => (
                                <PlaygroundApp
                                    key={id}
                                />
                            ))}
                            {/* <Editor key={id} quillId={id} /> */}
                            {/* <RichTextEditor isMobile={isMobile} key={id} editorId={id} /> */}
                            {!conflictPreviewVerion &&
                                <div className='mouseContainer'>
                                    <MouseContainer />
                                </div>
                            }
                        </div>
                    </div>
                    {/* Overlay button to reopen outline when collapsed */}
                    {!isOutlineOpen && <button className='outline-open-btn' onClick={() => setIsOutlineOpen(true)} title='Show document outline' aria-label='Show document outline'><EyeOutlined /></button>}
                </div>
                {!conflictPreviewVerion && <AiChatWrapper />}
                <DocumentDataConflictModal />
                <CreatePadDrawer
                    open={modals.createPad}
                    onClose={() => dispatch(closeCreatePadDrawer())}
                    onCreate={() => {
                        dispatch(closeCreatePadDrawer());
                    }}
                />

            </div>
            // </div>
        )
    }

}

export default MainLayout;